import React, { useState, useEffect } from 'react';
import { getLocale, useHistory } from 'umi';
import { Table, PagingQuery } from 'tea-component';
import CardSlotV2 from '@intelligence/components/CardSlotV2';
import FirstRow from '@intelligence/components/TableComponents/components/FirstRow';
import CustomTable from '@intelligence/components/TableComponents/CustomTable';
import DatabrainBubbleTip from '@intelligence/components/TableComponents/components/FirstRow/DatabrainBubbleTip';
// import InsideTable from './InsideTable';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { getCountryFlag } from '@home/_config/countries';
import { sliceTableCellGames } from '@intelligence/v3/utils/topcharts';
import { getDetailGamePathCommon } from '@intelligence/utils/topchartsUtils';
import CommonColumns, { DetailsCloums } from '@intelligence/pages/TopCharts/_config/TableCloums/CommonCloums';
import { MetricsTableCloums } from '@intelligence/pages/TopCharts/_config/TableCloums/MetricsTableCloums';
import {
  overlapAnalysisTalbeHeaderList,
  getOverlapMetricValueOfCvm,
} from '@intelligence/pages/OverlapAnalysis/MobileGames/utils';
import { DATASOURCE_METRIC_KEY_MAP } from '@intelligence/v3/_config/metrics';
import { SENSORTOWER_METRIC_KEY_MAP } from '@intelligence/v3/_config/metrics/keyMap';

import type { SortByItemType } from '@intelligence/components/TableComponents/components/SortBy';

import './index.scss';

import Databarin from '@intelligence/assets/svg/dataSource/databarin.svg';
// import AppstoreIcon from '@intelligence/assets/svg/dataSource/appstore.svg';
// import GoogleplayIcon from '@intelligence/assets/svg/dataSource/googleplay.svg';

const {
  // autotip,
  // expandable,
  scrollable,
} = Table.addons;

const defaultPageSize = 100;

// const platformIcons = {
//   ios: AppstoreIcon,
//   android: GoogleplayIcon,
// } as any;

const CROSS_METRICS = [SENSORTOWER_METRIC_KEY_MAP.CROSS_APP_USAGE, SENSORTOWER_METRIC_KEY_MAP.CROSS_AFFINITY];

const getComposedCrossGames = (key: string, game?: IMetricDataGame | null, gameDetailsMap?: IRecord<IRecord>) => {
  if (!game) {
    return [];
  }

  const crossGames = game[key] || [];

  if (!Array.isArray(crossGames) || crossGames.length === 0) {
    return [];
  }

  return crossGames.map((crossGame: any) => {
    const gameId = crossGame?.target_game_id || '';
    const crossGameDetails = gameDetailsMap?.[gameId] || {};
    return {
      ...crossGame,
      ...crossGameDetails,
      game_id: gameId,
      game_name: crossGameDetails?.entity_name || '',
    };
  });
};

interface IOverlapAnalysisTableProps extends IBaseProps, IGameDetailsMapInfo {
  loading: boolean;
  entityType: string;
  dateType?: string;
  genreSource: string;
  // fetchParams: any;
  tableTotal: number;
  records: any[];
  pageSortAttr: any;
  crossGameDetailsMapLoading: boolean;
  crossGameDetailsMap: IRecord<IRecord>;
  onTableSortChage: (e: SortByItemType) => void;
  onTablePagingChange: (e: PagingQuery) => void;
}

const OverlapAnalysisTable: React.FC<IOverlapAnalysisTableProps> = (props) => {
  const {
    loading,
    entityType,
    dateType,
    genreSource = '',
    // fetchParams,
    tableTotal = 0,
    records,
    gameDetailsMapLoading,
    gameDetailsMap,
    crossGameDetailsMapLoading,
    crossGameDetailsMap,
    pageSortAttr,
    onTableSortChage,
    onTablePagingChange,
  } = props;

  const loadingOrNodata = loading || !records?.length;
  const { tablePageIndex, tablePageSize, currentSortValue, currentSortMetric, currentRatioType } = pageSortAttr || {};

  const locale = getLocale();
  const { formatMessage } = useIntlExtend();

  const history = useHistory();

  // const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [pageIndex, setPageIndex] = useState<number>(tablePageIndex || 1);
  const [pageSize, setPageSize] = useState<number>(tablePageSize || defaultPageSize);
  const [columns, setColumns] = useState<any[]>([]);

  const getMetricColumns = () => {
    const metricColumns: any[] = [];
    overlapAnalysisTalbeHeaderList.forEach((t) => {
      metricColumns.push(
        MetricsTableCloums({
          loading: gameDetailsMapLoading || crossGameDetailsMapLoading,
          entityType,
          formatMessage,
          key: `${t?.value}`,
          mauToAvgMau: false,
          renderType: t?.renderType,
          // @ts-ignore
          align: t?.align,
          width: t?.width,
          dataSource: t?.dataSource,
          title: t?.text,
          textContent: t?.info,
          sortMetric: t?.value,
          currentSortValue,
          currentSortKey: `${currentSortMetric}_${currentRatioType}`,
          onSortChage,
          dateType,
          isNoDropdown: t?.isNoDropdown,
          getMetricValueOfCvm: (cvm: IMetricDataGame, metricKey: string, dataSourceKey: string) => {
            const newCrossAppUsageGames = getComposedCrossGames(
              DATASOURCE_METRIC_KEY_MAP.SENSORTOWER__CROSS_APP_USAGE,
              cvm,
              crossGameDetailsMap,
            );
            const newCrossAffinityGames = getComposedCrossGames(
              DATASOURCE_METRIC_KEY_MAP.SENSORTOWER__CROSS_AFFINITY,
              cvm,
              crossGameDetailsMap,
            );
            const newCvm = {
              ...cvm,
              [DATASOURCE_METRIC_KEY_MAP.SENSORTOWER__CROSS_APP_USAGE]: sliceTableCellGames(newCrossAppUsageGames),
              [DATASOURCE_METRIC_KEY_MAP.SENSORTOWER__CROSS_AFFINITY]: sliceTableCellGames(newCrossAffinityGames),
            };
            return getOverlapMetricValueOfCvm(newCvm, metricKey, dataSourceKey);
          },
          showSvgTip: !CROSS_METRICS.includes(t?.value),
        }),
      );
    });
    return metricColumns;
  };

  const onPagingChange = (e: PagingQuery) => {
    setPageIndex(e.pageIndex || 1);
    setPageSize(e.pageSize || defaultPageSize);
    onTablePagingChange(e);
  };

  const onSortChage = (e: SortByItemType) => {
    onTableSortChage(e);
  };

  const getDetailGamePath = (gameId: string) => {
    return getDetailGamePathCommon({
      entityType: 'mobile',
      gameId,
      tab: 'overlap',
      subTab: '',
    });
  };

  const onClickGame = (unifiedId: string) => {
    history.push(getDetailGamePath(unifiedId));
  };

  useEffect(() => {
    setPageIndex(tablePageIndex);
    setPageSize(tablePageSize);
  }, [tablePageIndex, tablePageSize]);

  useEffect(() => {
    const columns = [
      {
        key: 'select',
        width: 16,
        fixed: 'left',
        header: () => {
          return <></>;
        },
      },
      {
        key: 'gametitle',
        header: (
          <div className="dbi-firstRow-flex">
            <div className="dbi-text-id">#</div>
            <div>{formatMessage({ id: 'dbi_top_gametitle' })}</div>
          </div>
        ),
        width: 330,
        fixed: 'left',
        render: (cvm: any) => {
          // const platform = Array.isArray(cvm?.platform) ? cvm.platform[0] : (cvm?.platform || ');
          // const platformIcon = platformIcons[platform];
          const gameId = cvm?.game_id || '';
          const gameDetails = gameDetailsMap?.[gameId] || {};
          const gamePublisherCountryCode = gameDetails?.publisher_country_abbre || '';
          const newCvm = {
            img: cvm?.cover || '',
            gametitle: cvm?.game_name || '',
            countryIcon: !gamePublisherCountryCode ? '' : getCountryFlag(gamePublisherCountryCode),
            text: gameDetails?.publisher || '',
            isDatabrain: !gameDetails?.b_ogdb_data ? '' : Databarin,
            databrainIconTip: <DatabrainBubbleTip />,
            // frontIcon: platformIcon, // 隐藏平台图标
          };

          return (
            <FirstRow
              id={cvm?.insideTable ? <></> : cvm?.rank}
              cvm={newCvm}
              rihgtClickToJumpHref={getDetailGamePath(gameId)}
              onClick={() => {
                onClickGame(gameId);
              }}
            />
          );
        },
      },
      ...getMetricColumns(),
      CommonColumns.ReleaseTimeColumn({
        locale,
        formatMessage,
        placeholder: '',
        isGameDetailsMode: true,
        gameDetailsLoading: gameDetailsMapLoading,
        gameDetailsMapping: gameDetailsMap,
      }),
      // @ts-ignore
      CommonColumns.GenreColumn({
        placeholder: '',
        genreSource: genreSource as any,
        isGameDetailsMode: true,
        gameDetailsLoading: gameDetailsMapLoading,
        gameDetailsMapping: gameDetailsMap,
      }),
      DetailsCloums({
        onClickGame: (cvm: any) => {
          onClickGame(cvm?.game_id);
        },
        formatMessage,
      }),
    ];
    setColumns(columns);
  }, [locale, gameDetailsMapLoading, gameDetailsMap, crossGameDetailsMapLoading, crossGameDetailsMap]);

  return (
    <CardSlotV2 className="dbi-overlap-analysis-table-content">
      <div id="demographics-topcharts-table">
        <CustomTable
          className="dbi-mobile-table sub-table-same dbi-mobileGames-table dbi-table-sticky"
          // bordered="all"
          loading={loading}
          recordKey="game_id"
          records={records}
          columns={columns}
          tableTotal={tableTotal}
          pageSize={pageSize}
          pageIndex={pageIndex}
          onPagingChange={(e) => {
            onPagingChange?.(e);
          }}
          tableAddons={[
            // autotip({
            //   emptyText: 'No data under current selections',
            // }),
            scrollable({
              minWidth: 'auto',
              minHeight: loadingOrNodata ? '158px' : 'auto',
            }),
            // expandable({
            //   expandedKeys,
            //   gapCell: 0,
            //   shouldRecordExpandable: (record: any) => {
            //     return record?.app_ids?.length > 0;
            //   },
            //   gapCellRender: () => 'gapCellRender',
            //   onExpandedKeysChange: (keys: string[], { event }: any) => {
            //     event?.stopPropagation();
            //     setExpandedKeys(keys);
            //   },
            //   render(record: any) {
            //     return (
            //       <InsideTable
            //         unifiedId={record?.[UNIFIED_ID]}
            //         appids={record?.app_ids}
            //         fetchParams={fetchParams}
            //         columns={columns}
            //       />
            //     );
            //   },
            // }),
          ]}
        />
      </div>
    </CardSlotV2>
  );
};

export default OverlapAnalysisTable;
